import { Inject, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocationService } from './location.service';
import { PlatformSettingsService } from './platform-settings.service';
import { haversineKm } from './geo';

/**
 * Notices when the shopper has MOVED away from the delivery spot they chose, tells them, and reopens
 * the location picker.
 *
 * WHY: the chosen spot is not cosmetic. It decides which shops are listed, what the delivery fee is,
 * and where a rider is sent. A shopper who picked "home" in the morning, travelled to the office and
 * ordered lunch would have it delivered to their house — and would only find out when it failed. The
 * threshold is the operator's (PlatformPollingSettings.CustomerLocationDriftMeters).
 *
 * THREE RULES that matter more than the feature itself:
 *
 *  1. NEVER PROVOKE A PERMISSION PROMPT. We check only when geolocation is ALREADY granted. Asking a
 *     browsing shopper for their location so we can second-guess their address would be a worse
 *     experience than the bug this prevents. Where the Permissions API is missing we stay silent
 *     rather than guess, because getCurrentPosition would prompt.
 *  2. ASK ONCE PER MOVE. The picker is a modal; reopening it on a timer would make the app unusable.
 *     We remember the position we last warned about and stay quiet until they move again.
 *  3. NEVER OVERRIDE THEIR CHOICE. We inform and open the picker. If the shopper dismisses it, their
 *     spot stands — a customer ordering to somewhere they are not is completely legitimate (a gift,
 *     dinner sent home while still at work).
 */
@Injectable({ providedIn: 'root' })
export class LocationDriftService {
  private readonly loc = inject(LocationService);
  private readonly settings = inject(PlatformSettingsService);
  private readonly isBrowser: boolean;

  /** How far they have moved, in metres, when we are telling them about it. Null = nothing to say. */
  readonly driftMeters = signal<number | null>(null);

  /** Don't re-check more often than this, however many times the app regains focus. */
  private static readonly MIN_CHECK_GAP_MS = 60_000;

  /** A fix this stale is not evidence of where they are now. */
  private static readonly MAX_FIX_AGE_MS = 5 * 60_000;

  private lastCheckedAt = 0;
  /** The device position we last warned about, so rule 2 holds across checks. */
  private warnedAt: { lat: number; lng: number } | null = null;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Start watching. Called once from the layout. Re-checks when the tab regains focus, which is when a
   * shopper who has physically moved comes back to the app — far more useful than a timer, and free.
   */
  start(): void {
    if (!this.isBrowser) return;
    void this.check();
    window.addEventListener('focus', () => void this.check());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.check();
    });
  }

  /** Dismiss the notice. Their chosen spot stands — see rule 3. */
  dismiss(): void {
    this.driftMeters.set(null);
  }

  /**
   * Called when the shopper picks a spot, so a fresh choice re-arms the check. Without this, someone
   * who moved, was warned, and then chose their new location would never be warned again on the next
   * journey.
   */
  reset(): void {
    this.warnedAt = null;
    this.driftMeters.set(null);
  }

  private async check(): Promise<void> {
    const threshold = this.settings.locationDriftMeters;
    if (threshold <= 0) return;                      // operator turned it off

    const chosen = this.loc.location();
    if (!chosen) return;                             // nothing to drift FROM
    if (this.loc.pickerOpen()) return;               // they're already choosing

    const now = Date.now();
    if (now - this.lastCheckedAt < LocationDriftService.MIN_CHECK_GAP_MS) return;
    this.lastCheckedAt = now;

    if (!(await this.permissionGranted())) return;   // rule 1

    const fix = await this.currentPosition();
    if (!fix) return;

    const metres = haversineKm(chosen.lat, chosen.lng, fix.lat, fix.lng) * 1000;
    if (metres <= threshold) return;

    // Rule 2: already warned from roughly here? Compare against the position we warned at, not the
    // chosen spot — otherwise every check past the threshold would re-open the picker.
    if (this.warnedAt) {
      const moveSinceWarning = haversineKm(this.warnedAt.lat, this.warnedAt.lng, fix.lat, fix.lng) * 1000;
      if (moveSinceWarning <= threshold) return;
    }

    this.warnedAt = fix;
    this.driftMeters.set(Math.round(metres));
    this.loc.openPicker(false);
  }

  /**
   * True only when geolocation is already granted. `permissions.query` is what makes rule 1 possible:
   * it answers without prompting, whereas getCurrentPosition would ask. Safari lacks it for
   * geolocation in some versions — there we return false and the feature simply never runs.
   */
  private async permissionGranted(): Promise<boolean> {
    try {
      const perms = (navigator as any).permissions;
      if (!perms?.query) return false;
      const status = await perms.query({ name: 'geolocation' as PermissionName });
      return status.state === 'granted';
    } catch {
      return false;
    }
  }

  private currentPosition(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        // A cached fix is fine and cheap: we are detecting a journey, not tracking someone. maximumAge
        // lets the browser answer from its last fix rather than waking the GPS on every tab focus.
        { timeout: 8000, maximumAge: LocationDriftService.MAX_FIX_AGE_MS, enableHighAccuracy: false },
      );
    });
  }
}
